import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { LOGO_URL, SITE_URL, categories, getProductSlugFromPath, getRouteMeta } from '../utils/seo';


const Webshop = () => {
  const location = useLocation();
  // Temporary hotfix: disable the webshop register page.
  if (location.pathname === '/webshop/register' || location.pathname.startsWith('/webshop/register/')) {
    return <Navigate to="/" replace />;
  }
  const productSlug = getProductSlugFromPath(location.pathname);
  const prerenderProduct = typeof window !== 'undefined' && window.__SEO_PRODUCT__?.webshopSlug === productSlug
    ? window.__SEO_PRODUCT__
    : null;
  const meta = getRouteMeta(location.pathname, prerenderProduct);
  const canonicalUrl = `${SITE_URL}${meta.canonicalPath}`;
  const categoryItems = categories.map((category, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    url: `${SITE_URL}/webshop/category/${category.slug}`,
    name: category.name,
  }));
  const breadcrumbItems = [
    { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
    { '@type': 'ListItem', position: 2, name: 'Webshop', item: `${SITE_URL}/webshop` },
  ];

  if (meta.category) {
    breadcrumbItems.push({
      '@type': 'ListItem',
      position: 3,
      name: meta.category.name,
      item: canonicalUrl,
    });
  } else if (meta.type === 'product') {
    breadcrumbItems.push({
      '@type': 'ListItem',
      position: 3,
      name: meta.h1,
      item: canonicalUrl,
    });
  }

  const schemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Timmers BV',
      url: SITE_URL,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${SITE_URL}/webshop/products?searchQuery={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    },
  ];

  if (meta.type === 'category') {
    schemas.push(
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbItems,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: meta.h1,
        itemListElement: categoryItems,
      },
    );
  }

  if (meta.type === 'product' && prerenderProduct) {
    schemas.push(
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbItems,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: meta.h1,
        description: meta.description,
        image: meta.image,
        sku: prerenderProduct.articleNumber || prerenderProduct.id,
        url: canonicalUrl,
        brand: {
          '@type': 'Brand',
          name: 'Timmers BV',
        },
      },
    );
  }

  useEffect(() => {
    // --- TASK: Load Product Details (from product.js) ---
    const loadProduct = async () => {
      const params = new URLSearchParams(location.search);
      const slug = params.get('searchQuery');
      const productContainer = document.getElementById('product');

      if (!productContainer) return;

      if (!slug) {
        productContainer.innerHTML = '';
        return;
      }

      try {
        const response = await fetch('https://timmers.bakaglass.nl/graphql/webshop/batch');
        const products = await response.json();
        const product = products.find(p => p.slug === slug);

        if (!product) {
          productContainer.innerHTML = '<p>Product niet gevonden.</p>';
          return;
        }

        document.title = product.name;
        productContainer.innerHTML = `
          <div class="custom-product-detail">
            <h1 id="product-name">${product.name}</h1>
            <img id="product-image" src="${product.image}" alt="${product.name}" style="max-width: 100%; height: auto;" />
            <p id="product-description">${product.description || ''}</p>
            <div id="product-price">${product.price || ''}</div>
          </div>
        `;
      } catch (error) {
        console.error("Error loading product:", error);
        productContainer.innerHTML = '<p>Fout bij het laden van het product.</p>';
      }
    };

    loadProduct();
  }, [location.search]);

  useEffect(() => {
    // --- TASK: UI Enhancements (Filters, Titles from main.js) ---
    const formatHighlightLinks = () => {
      const links = document.querySelectorAll('.webshop-home-highlight-productgroup-card__link');
      links.forEach(link => {
        const href = link.getAttribute('href');
        if (href && href.includes(' ')) {
          link.setAttribute('href', href.replace(/\s+/g, ''));
        }
      });
    };

    const addProductGroupTitle = () => {
      if (document.querySelector('.custom-product-group-title')) return;
      const filterContainer = document.querySelector('.webshop-productgroup__page__grid');
      if (!filterContainer) return;

      const path = window.location.pathname;
      if (path.includes('/category/')) {
        // Get all parts after /category/ and take the last one
        const pathParts = path.split('/category/')[1].split('/').filter(part => part.length > 0);
        let groupName = pathParts[pathParts.length - 1]; // Get the last segment
        if (groupName) {
          groupName = groupName.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          const titleElement = document.createElement('h3');
          titleElement.className = 'custom-product-group-title';
          titleElement.textContent = groupName;
          filterContainer.parentNode.insertBefore(titleElement, filterContainer);
        }
      }
    };

    const addFilterToggle = () => {
      if (document.getElementById('filter-toggle')) return;
      const filterWidget = document.querySelector('.webshop-product-index-filter-container__widget');
      if (!filterWidget) return;

      const toggleButton = document.createElement('button');
      toggleButton.id = 'filter-toggle';
      toggleButton.textContent = 'Filters tonen/verbergen';
      toggleButton.className = 'button black-alt small'; // Using project styles
      toggleButton.style.marginBottom = '1rem';

      toggleButton.addEventListener('click', (e) => {
        e.preventDefault();
        filterWidget.classList.toggle('is-open');
      });

      filterWidget.parentNode.insertBefore(toggleButton, filterWidget);
    };

    const addExpertButtons = () => {
      const formulier = document.querySelector('.webshop-product-detail__form');

      const readInStock = () => {
        try {
          const raw = window.localStorage?.getItem('bakaWebshopProductInStock');
          if (raw == null) return null;
          if (raw === 'true') return true;
          if (raw === 'false') return false;
          const parsed = JSON.parse(raw);
          return typeof parsed === 'boolean' ? parsed : null;
        } catch {
          return null;
        }
      };

      const updateUspStockRow = () => {
        const uspBlock = document.querySelector('.product-detail-usps');
        if (!uspBlock) return;
        const stockRow = uspBlock.querySelector('[data-usp-stock-row]');
        const stockText = uspBlock.querySelector('[data-usp-stock-text]');
        const stockIcon = uspBlock.querySelector('[data-usp-stock-icon]');
        if (!stockRow || !stockText || !stockIcon) return;

        const inStock = readInStock();
        const desiredDisplay = inStock === null ? 'none' : 'flex';
        if (stockRow.style.display !== desiredDisplay) stockRow.style.display = desiredDisplay;
        if (inStock === null) return;

        const desiredText = inStock ? 'Op voorraad' : 'Niet op voorraad';
        const desiredStroke = inStock ? '#10b981' : '#ef4444';
        if (stockText.textContent !== desiredText) stockText.textContent = desiredText;
        if (stockIcon.getAttribute('stroke') !== desiredStroke) stockIcon.setAttribute('stroke', desiredStroke);
      };

      // Altijd proberen te updaten (ook als het formulier nog niet in de DOM staat),
      // zodat we geen eindeloze MutationObserver-loop veroorzaken met onnodige writes.
      updateUspStockRow();

      if (!formulier) return;

      if (!document.querySelector('.expert-knop-extra')) {

        const expertKnop = document.createElement('a');
        expertKnop.href = "/contact";
        expertKnop.className = "expert-knop-extra button black-alt w-inline-block";
        expertKnop.innerHTML = '<div>Vraag een expert</div>';
        formulier.insertAdjacentElement('afterend', expertKnop);

        const montageLink = document.createElement('a');
        montageLink.textContent = "Montagehandleiding";
        montageLink.href = "#";
        montageLink.target = "_blank";
        montageLink.className = "montage-link-extra";
        montageLink.style.display = "block";
        montageLink.style.textDecoration = "underline";
        montageLink.style.cursor = "pointer";
        expertKnop.insertAdjacentElement('afterend', montageLink);

        // Inject USP block next to expert button
        if (!document.querySelector('.product-detail-usps')) {
          const uspBlock = document.createElement('div');
          uspBlock.className = 'product-detail-usps';
          uspBlock.innerHTML = `
            <div style="padding: 16px; background: #fafafa; border: 1px solid #eee; border-radius: 8px;">
              <div data-usp-stock-row style="display: none; align-items: center; gap: 8px; margin-bottom: 8px; font-size: 13px; color: #333;">
                <svg data-usp-stock-icon="" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#10b981" stroke-width="1" style="flex: 0 0 auto;"><path xmlns="http://www.w3.org/2000/svg" d="M13.5,5c-0.0762,0.0003-0.1514-0.0168-0.22-0.05L7.5,2L1.72,4.93C1.4632,5.0515,1.1565,4.9418,1.035,4.685&#10;&#9;S1.0232,4.1215,1.28,4L7.5,0.92L13.72,4c0.2761,0.0608,0.4508,0.3339,0.39,0.61C14.0492,4.8861,13.7761,5.0608,13.5,5z M5,10H2v3h3&#10;&#9;V10z M9,10H6v3h3V10z M13,10h-3v3h3V10z M11,6H8v3h3V6z M7,6H4v3h3V6z"/></svg>
                <span data-usp-stock-text style="text-align: start;">Voorraad</span>
              </div>
              <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px; font-size: 13px; color: #333;">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" stroke-width="2"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                <span style="text-align: start;">Eigen transport door heel NL & Vlaanderen</span>
              </div>
              <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px; font-size: 13px; color: #333;">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72"/></svg>
                <span style="text-align: start;">Klantenservice: 0162 - 46 11 75</span>
              </div>
              <div style="display: flex; align-items: center; gap: 8px; font-size: 13px; color: #333;">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" stroke-width="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
                <span style="text-align: start;">Retourneren binnen 14 dagen mogelijk</span>
              </div>
            </div>
          `;
          montageLink.insertAdjacentElement('afterend', uspBlock);
        }
      }
    };

    const moveDescription = () => {
      const widget = document.querySelector('.webshop--product-detail__widget');
      const desc = document.querySelector('.webshop-product-detail-description__container');
      const targetColumn = document.querySelector('.webshop-product-details__grid > div:first-of-type');

      if (targetColumn && desc) {
        if (desc.parentNode !== targetColumn) {
          // Move it to the first column of the grid (under images)
          targetColumn.appendChild(desc);
        }
      } else if (widget && desc && desc.parentNode !== widget) {
        // Fallback to widget root if grid isn't found
        widget.appendChild(desc);
      }
    };

    const addRelatedProductsTitle = () => {
      if (document.querySelector('.custom-related-title')) return;
      const relatedWidget = document.querySelector('.webshop-related-products__widget');
      if (!relatedWidget) return;

      const titleElement = document.createElement('h2');
      titleElement.className = 'custom-related-title';
      titleElement.innerHTML = 'Vergelijkbare producten';
      relatedWidget.parentNode.insertBefore(titleElement, relatedWidget);
    };

    const wrapBackAndCategories = () => {
      const childGroupContainer = document.querySelector('.webshop-productgroup__page-productgroup-child-group__container');
      const backContainer = document.querySelector('.webshop-productgroup__page__back-container');

      if (childGroupContainer && backContainer && !childGroupContainer.parentNode.classList.contains('custom-back-categories-wrapper')) {
        const wrapper = document.createElement('div');
        wrapper.className = 'custom-back-categories-wrapper';
        wrapper.style.display = 'flex';
        wrapper.style.alignItems = 'flex-start';
        wrapper.style.gap = '20px';
        wrapper.style.width = '100%';

        childGroupContainer.parentNode.insertBefore(wrapper, childGroupContainer);

        wrapper.appendChild(backContainer);
        wrapper.appendChild(childGroupContainer);
      }
    };

    const moveBreadcrumbs = () => {
      const widget = document.querySelector('.webshop--product-detail__widget');
      const breadcrumbs = document.querySelector('.webshop-product-detail__breadcrumbs');

      if (widget && breadcrumbs && breadcrumbs.parentNode !== widget) {
        // Prepend it to the widget so it sits above the flex rows
        widget.insertBefore(breadcrumbs, widget.firstChild);
      }
    };

    const handleRegistrationPage = () => {
      const businessRadio = document.querySelector('#business');
      const privateRadio = document.querySelector('#private');

      if (businessRadio && !businessRadio.checked) {
        businessRadio.click();
      }
    };

    const updateSliderRange = () => {
      const sliders = document.querySelectorAll('input[type="range"]');
      sliders.forEach(slider => {
        if (slider.id.includes('weight')) {
          const actualMax = slider.getAttribute('max');
          const hasInitialValueAttribute = slider.hasAttribute('value');

          // Alleen fixen bij het eerste laden als het systeem een waarde heeft gezet
          // maar wij onze fix nog niet hebben toegepast.
          if (!slider.hasAttribute('data-range-initial-fix') && actualMax && actualMax !== "100" && hasInitialValueAttribute) {
            if (slider.id.includes('fromSlider')) {
              slider.value = slider.min || "0";
            } else if (slider.id.includes('toSlider')) {
              slider.value = actualMax;
            }

            slider.setAttribute('data-range-initial-fix', 'true');
            slider.setAttribute('data-range-fixed', 'true');

            slider.dispatchEvent(new Event('input', { bubbles: true }));
            slider.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
    };

    const observer = new MutationObserver(() => {
      formatHighlightLinks();
      addProductGroupTitle();
      addFilterToggle();
      addExpertButtons();
      moveDescription();
      moveBreadcrumbs();
      handleRegistrationPage();
      updateSliderRange();
      addRelatedProductsTitle();
      wrapBackAndCategories();
    });

    observer.observe(document.body, { childList: true, subtree: true, attributes: true });

    // Initial run
    formatHighlightLinks();
    addProductGroupTitle();
    addFilterToggle();
    addExpertButtons();
    handleRegistrationPage();
    updateSliderRange();
    addRelatedProductsTitle();
    wrapBackAndCategories();

    return () => observer.disconnect();
  }, [location.pathname]);

  return (
    <>
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:image" content={meta.image || LOGO_URL} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:locale" content="nl_NL" />
        {schemas.map((schema, index) => (
          <script key={index} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}
      </Helmet>
      <main className="webshop-page">
        <baka-products></baka-products>
        <div id="product" className="container"></div>

      </main>
    </>
  );
};

export default Webshop;
